import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Paper, Box, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Loader } from 'ui';
import { format } from 'date-fns';
import { fetchEventsPropertiesAction, fetchEventTitles } from 'modules/events/store/eventsActions';
import { addNotificationAction } from 'store/notifications/notificationsActions';
import { Event } from 'modules/events/types/eventsTypes';
import { EventEdit } from '../components/EventsCreationPanel/EventEdit/EventEdit';
import { EventPreview } from '../components/EventsCreationPanel/EventPreview';
import { fetchEventDetails, saveEvent, updateEvent } from '../services/eventsService';
import { getEventProperties } from '../selectors/eventsSelectors';
import { updateSessionId } from '../constants/eventsUpdate';

const steps = ['Edit event', 'Preview', 'Finish'];

type EventPanelCreateParams = {
  id?: string;
};

type FormikValues = null | object;

export const EventPanelCreate: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [initialValues, setInitialValues] = useState<FormikValues>(null);
  const [event, setEvent] = useState<Event>();
  const [editedEventDetails, setEditedEventDetails] = useState<Event>();
  const [loading, setLoading] = useState(false);
  const properties = useSelector(getEventProperties);
  const { id } = useParams<EventPanelCreateParams>();

  const goNext = () => setActiveStep((step) => step + 1);
  const goPrev = () => setActiveStep((step) => step - 1);

  useEffect(() => {
    dispatch(fetchEventsPropertiesAction());
    dispatch(fetchEventTitles(''));
  }, [dispatch]);

  const handleEventUpdate = (eventPreview: Event, values: object) => {
    setInitialValues(values);

    if (editedEventDetails) {
      const { documentList } = editedEventDetails;
      setEvent({ ...eventPreview, id, documentList });
    } else {
      setEvent(eventPreview);
    }

    goNext();
  };

  const submitEvent = () => {
    setLoading(true);

    const request = event?.id ? updateEvent(event!) : saveEvent(event!);

    request
      .then(() => {
        navigate('/event-panel/view');
      })
      .catch((error: any) => {
        setLoading(false);
        dispatch(
          addNotificationAction({
            message: error.message,
            variant: 'error',
          }),
        );
      });
  };

  useEffect(() => {
    if (properties.isPending) return;

    if (!id) {
      setInitialValues({});
      return;
    }

    fetchEventDetails(id).then((values) => {
      const { data: options } = properties;

      fetch(`data:image/png;base64,${values.image}`)
        .then((res) => res.blob())
        .then((blob) => {
          setEditedEventDetails(values);

          setInitialValues({
            event: { id: values.externalId, name: values.title },
            session: {
              id: updateSessionId,
              name: format(new Date(values.eventDate), 'd MMMM yyyy - EEEE h:mm a'),
            },
            dateTime: values.eventDate,
            duration: values.duration,
            location: options.locations.find(({ name }) => name === values.location.name),
            language: values.languages,
            category: options.categories.find((item) => item.id === values.category),
            segment: options.segments.find(({ name }) => name === values.segment),
            productLine: options.productLines.find(({ name }) => name === values.productLine),
            industry: options.industries.find(({ name }) => name === values.industry),
            region: options.regions.find(({ name }) => name === values.region),
            marketingLabel: values.marketingLabel,
            description: values.description,
            activeEvent: values.status === 'ACTIVE',
            logo: new File([blob], 'logo.png', { type: 'image/png' }),
          });
        });
    });
  }, [id, properties]);

  if (!initialValues || properties.isPending) {
    return <Loader />;
  }

  return (
    <>
      <Box mb={2}>
        <Typography variant="h5">{t('Create event')}</Typography>
      </Box>
      <Paper elevation={3}>
        <Box pt={4}>
          <Stepper alternativeLabel activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        {loading ? (
          <Box my={4}>
            <Loader center />
          </Box>
        ) : (
          <>
            {activeStep === 0 && (
              <EventEdit
                initialValues={initialValues}
                editedEventDetails={editedEventDetails}
                onEventUpdate={handleEventUpdate}
              />
            )}
            {activeStep === 1 && event && (
              <EventPreview event={event} onBack={goPrev} onSubmit={submitEvent} />
            )}
          </>
        )}
      </Paper>
    </>
  );
};
